// Phase I0.3 — ConfMeter primitive.
// label + numeric value + thin progress bar. `locked` variant blurs the
// value for paid-tier teaser surfaces (free users see the bar shape but
// not the number).

import type { CSSProperties } from "react";

export interface ConfMeterProps {
  /** 0–100. Clamped on render. */
  value: number;
  /** Hide the numeric value behind a blur. */
  locked?: boolean;
  /** Optional label override. Defaults to "CONFIDENCE". */
  label?: string;
  /** Bar height in pixels. Default 6. */
  height?: number;
  style?: CSSProperties;
}

export function ConfMeter({ value, locked, label, height = 6, style }: ConfMeterProps) {
  const pct = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
  return (
    <div style={{ width: "100%", ...style }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10,
            letterSpacing: "0.18em",
            color: "var(--sa-fg-muted)",
            textTransform: "uppercase",
          }}
        >
          {label ?? "Confidence"}
        </span>
        <span
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 13,
            color: locked ? "var(--sa-fg-muted)" : "var(--sa-fg-primary)",
            letterSpacing: 0.4,
            filter: locked ? "blur(5px)" : "none",
            userSelect: "none",
          }}
        >
          {locked ? "88/100" : `${Math.round(pct)}/100`}
        </span>
      </div>
      <div
        style={{
          height,
          background: "var(--sa-hairline-bright)",
          borderRadius: 1,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: locked
              ? "repeating-linear-gradient(90deg, var(--sa-amber-dim) 0 6px, transparent 6px 10px)"
              : "var(--sa-accent-saffron)",
            transition: "width .6s cubic-bezier(.4,0,.2,1)",
          }}
        />
      </div>
    </div>
  );
}
